import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getHeadlines } from "./api";
import type { GetHeadlinesParams } from "./types";
import useAuth from "@/hooks/useAuth";
import { useMemo, useEffect, useState, useRef } from "react";
import type {
  Headline,
  HeadlineTimestamp,
} from "@/widget-components/newsquawk-headlines/models/Headline";
import headlinesSSEClient from "./headlinesSSEClient";
import { playChime, playSpoon } from "@/utils/sound-utils";
import { useUserPreferences } from "@/pages/settings/hooks/useUserPreferences";
import type { ConnectionStatus } from "@/shared/services/configurable-sse-client";
import { useHeadlinesPortfoliosWithSoundEnabled } from "./useHeadlinesPortfoliosWithSoundEnabled";
import { headlinePortfolioMatcher } from "@/widget-components/newsquawk-headlines/utils";

/** Maximum headlines to keep in the first page of the infinite query */
const MAX_FIRST_PAGE = 200;

interface UseHeadlinesOptions extends GetHeadlinesParams {
  subscribe?: boolean;
}

/**
 * Determines if a headline should be hidden from the feed.
 */
const shouldHideHeadline = (headline: Headline): boolean => {
  // Hidden flag explicitly set
  if (headline.hidden) {
    return true;
  }

  // Capital-only headlines (appears_on_capital flag)
  if (headline.appears_on_capital) {
    return true;
  }

  return false;
};

/**
 * Gets the Unix timestamp from a headline's published_at field.
 * Handles both HeadlineTimestamp object and ISO string formats.
 */
const getUnixTimestamp = (publishedAt: HeadlineTimestamp | string): number => {
  if (typeof publishedAt === "string") {
    return Date.parse(publishedAt) / 1000;
  }
  return publishedAt.unix;
};

export const useHeadlines = (options?: UseHeadlinesOptions) => {
  const { subscribe = true, ...params } = options || {};
  const { authenticated } = useAuth();
  const { cursor, ...restParams } = params || {};
  const queryClient = useQueryClient();

  // Use ref to store restParams for stable reference in effect
  const restParamsRef = useRef(restParams);
  restParamsRef.current = restParams;

  const [subscribeStatus, setSubscribeStatus] =
    useState<ConnectionStatus>("inactive");

  // Track previous status to detect reconnection
  const prevSubscribeStatusRef = useRef<ConnectionStatus>("inactive");

  const { notificationsSound, isLoading: isUserPrefLoading } =
    useUserPreferences();

  // Keep a stable reference to portfolios with sound enabled (for watchlist alerts)
  const portfoliosWithSoundEnabledRef = useHeadlinesPortfoliosWithSoundEnabled();

  const headlinesQuery = useInfiniteQuery({
    queryKey: ["headlines", restParams],
    queryFn: ({ pageParam }) =>
      getHeadlines({
        ...restParams,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      // Return the next cursor if there are more pages
      return lastPage.pagination.has_more
        ? lastPage.pagination.next_cursor
        : undefined;
    },
    initialPageParam: cursor as string | undefined,
    enabled: authenticated,

    // refetch configs
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Flatten all pages into a single array of headlines, sorted chronologically
  const headlines = useMemo(() => {
    if (!headlinesQuery.data?.pages) return [];
    const all = headlinesQuery.data.pages.reduce((acc: Headline[], page) => {
      return [...acc, ...page.data];
    }, []);
    // Defensive sort — ensures consistent chronological order regardless of
    // whether data came from SSE insertion, polling, or a background refetch
    return all.sort((a, b) => {
      const aTime = typeof a.published_at === "object" ? a.published_at.unix * 1000 : new Date(a.published_at).getTime();
      const bTime = typeof b.published_at === "object" ? b.published_at.unix * 1000 : new Date(b.published_at).getTime();
      return bTime - aTime; // newest first
    });
  }, [headlinesQuery.data]);

  // Get the last page's pagination info
  const pagination = useMemo(() => {
    if (!headlinesQuery.data?.pages) return undefined;
    const lastPage =
      headlinesQuery.data.pages[headlinesQuery.data.pages.length - 1];
    return lastPage?.pagination;
  }, [headlinesQuery.data]);

  const hasMore = useMemo(() => {
    return pagination?.has_more ?? false;
  }, [pagination]);

  const loadNextPage = () => {
    if (hasMore && !headlinesQuery.isFetchingNextPage) {
      headlinesQuery.fetchNextPage();
    }
  };

  const canSubscribe =
    subscribe && authenticated && !isUserPrefLoading;
  /**
   * Handle incoming headline messages from SSE
   */
  useEffect(() => {
    if (!canSubscribe) return;

    const messageHandler = (data: Headline) => {
      if (data) {
        try {
          // Shared callback for both live SSE pushes and polled/catch-up
          // items — don't assert the transport here.
          console.log("Received new headline:", data);

          const newHeadline = data as Headline;

          // Filter out hidden headlines using the shouldHideHeadline function
          if (shouldHideHeadline(newHeadline)) {
            return;
          }

          // Attach metadata
          newHeadline.isNew = true;
          newHeadline.received_at = Date.now();

          // Update the query cache — sound is played AFTER dedup check
          queryClient.setQueryData(
            ["headlines", restParamsRef.current],
            (oldData: InfiniteData<{ data: Headline[] }>) => {
              if (!oldData) return oldData;

              // Check if headline already exists in any page
              const headlineExists = oldData.pages.some((page) =>
                page.data.some((h: Headline) => h.id === newHeadline.id)
              );

              if (headlineExists) {
                // Check if content actually changed (not just a re-delivery)
                const existingHeadline = oldData.pages
                  .flatMap((p) => p.data)
                  .find((h: Headline) => h.id === newHeadline.id);

                // Fast path: if updated_at changed, the server modified something — just replace
                const existingUpdatedAt = typeof existingHeadline?.updated_at === "object"
                  ? existingHeadline.updated_at.unix
                  : 0;
                const newUpdatedAt = typeof newHeadline.updated_at === "object"
                  ? newHeadline.updated_at.unix
                  : 0;
                const updatedAtChanged = newUpdatedAt > 0 && existingUpdatedAt > 0 && newUpdatedAt !== existingUpdatedAt;

                // Slow path: updated_at is the same or unparseable — check display-relevant fields
                // existingHeadline is guaranteed defined here: the headlineExists
                // guard above uses the same id predicate over the same pages, so
                // the find() above always matches. Non-null assert (no behavior change).
                const hasChanged = updatedAtChanged ||
                  existingHeadline!.subject !== newHeadline.subject ||
                  existingHeadline!.content !== newHeadline.content ||
                  JSON.stringify(existingHeadline!.tag_id_list) !== JSON.stringify(newHeadline.tag_id_list);

                if (!hasChanged) {
                  // Same content — SSE re-delivery, skip silently
                  return oldData;
                }

                // Genuine content update — mark for white update flash
                newHeadline.wasUpdated = true;
                newHeadline.received_at = Date.now();
                console.log("Updating existing headline via SSE:", newHeadline);
                return {
                  ...oldData,
                  pages: oldData.pages.map((page) => ({
                    ...page,
                    data: page.data.map((h: Headline) =>
                      h.id === newHeadline.id ? newHeadline : h
                    ),
                  })),
                };
              }

              // Genuinely new headline — play sounds
              if (notificationsSound.headlines) playChime();
              const portfoliosToMatch = portfoliosWithSoundEnabledRef.current ?? [];
              if (portfoliosToMatch.length > 0) {
                const matchedPortfolio = headlinePortfolioMatcher(
                  newHeadline,
                  portfoliosToMatch
                );
                if (matchedPortfolio) {
                  playSpoon();
                }
              }

              // Insert at correct chronological position in first page
              const newTimestamp = getUnixTimestamp(newHeadline.published_at);
              const firstPage = oldData.pages[0];

              // Find correct position based on published_at (descending order - newest first)
              const insertIndex = firstPage.data.findIndex(
                (h: Headline) => getUnixTimestamp(h.published_at) <= newTimestamp
              );

              // If insertIndex is -1, headline is older than all in first page
              // Insert at end of first page (or could belong in a later page, but we keep it here for simplicity)
              const insertAt =
                insertIndex === -1 ? firstPage.data.length : insertIndex;

              const newFirstPageData = [
                ...firstPage.data.slice(0, insertAt),
                newHeadline,
                ...firstPage.data.slice(insertAt),
              ];

              return {
                ...oldData,
                pages: oldData.pages.map((page, index: number) =>
                  index === 0
                    ? {
                        ...page,
                        data: newFirstPageData.length > MAX_FIRST_PAGE
                          ? newFirstPageData.slice(0, MAX_FIRST_PAGE)
                          : newFirstPageData,
                      }
                    : page
                ),
              };
            }
          );
        } catch (error) {
          console.error("Error processing new headline:", error);
        }
      }
    };

    const sub1 = headlinesSSEClient.subscribeTopic({
      topic: "headline",
      callback: messageHandler,
    });

    const sub2 = headlinesSSEClient.onStatusChange(setSubscribeStatus);

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
    // Note: restParams excluded - using ref to avoid effect re-runs on object reference changes
  }, [
    subscribe,
    queryClient,
    notificationsSound.headlines,
    canSubscribe,
  ]);

  /**
   * Refetch headlines when connection re-establishes after being inactive
   * This ensures we get any headlines that were published during SSE downtime
   */
  useEffect(() => {
    const prevStatus = prevSubscribeStatusRef.current;
    const currentStatus = subscribeStatus;

    // Update the ref for next time
    prevSubscribeStatusRef.current = currentStatus;

    // If we transitioned from inactive/pending to active (reconnection)
    if (
      (prevStatus === "inactive" || prevStatus === "pending") &&
      currentStatus === "active" &&
      canSubscribe
    ) {
      console.log(
        "[useHeadlines] Connection re-established, refetching headlines to catch up..."
      );
      headlinesQuery.refetch();
    }
  }, [subscribeStatus, canSubscribe, headlinesQuery]);

  return {
    headlines,
    pagination,
    hasMore,
    loadNextPage,
    loadingNextPage: headlinesQuery.isFetchingNextPage,
    isLoading: headlinesQuery.isLoading,
    isError: headlinesQuery.isError,
    error: headlinesQuery.error,
    subscribeStatus,
    headlinesQuery,
  };
};
